import { useCallback, useEffect, useRef } from "react";

/**
 * Delays side effects until a dialog is actually closed.
 *
 * Why this hook exists:
 * MUI `Modal`/`Dialog` blocks page scroll while open. If navigation, sign-out, or another
 * route-changing side effect runs in the same handler that closes the dialog, the current page
 * can unmount before MUI restores `body` scroll styles. The visible result is a page without
 * scroll after the dialog is gone.
 *
 * How to use it:
 * 1. Call `closeAndRun(action)` instead of `onClose(); action();`.
 * 2. For plain `Modal` components, that is enough. The hook runs the pending action after
 *    `isOpen` becomes `false`.
 * 3. For transition-based dialogs such as MUI `Dialog`, also pass `transitionSlotProps` to the
 *    dialog transition slot. In that case the pending action runs from `onExited`, after the
 *    exit transition and MUI scroll-lock cleanup have finished.
 *
 * @param {Object} params Hook configuration.
 * @param {boolean} params.isOpen Current dialog open state.
 * @param {Function} params.onClose Function that closes the dialog.
 * @param {boolean} [params.waitForExitTransition=false]
 * Whether to wait for a transition `onExited` callback before running the pending action.
 * Set this to `true` for MUI `Dialog`, which removes scroll lock after its exit transition.
 * @returns {{
 *   closeAndRun: (action: Function) => void,
 *   transitionSlotProps: ({ onExited: Function } | undefined)
 * }}
 */
const useActionAfterDialogClose = ({ isOpen, onClose, waitForExitTransition = false }) => {
    const pendingActionRef = useRef();
    const timeoutRef = useRef();

    const runPendingAction = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        }
        const pendingAction = pendingActionRef.current;
        pendingActionRef.current = undefined;
        pendingAction?.();
    }, []);

    const closeAndRun = useCallback(
        (action) => {
            pendingActionRef.current = action;
            onClose();
        },
        [onClose]
    );

    const handleTransitionExited = useCallback(() => {
        if (pendingActionRef.current) {
            runPendingAction();
        }
    }, [runPendingAction]);

    useEffect(() => {
        if (!isOpen && pendingActionRef.current && !waitForExitTransition) {
            // MUI removes body scroll lock from its own effect. Deferring to the next tick
            // avoids navigating away before `ModalManager.remove()` restores document styles.
            timeoutRef.current = setTimeout(runPendingAction, 0);
        }
    }, [isOpen, runPendingAction, waitForExitTransition]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return {
        closeAndRun,
        transitionSlotProps: waitForExitTransition
            ? { onExited: handleTransitionExited }
            : undefined,
    };
};

export default useActionAfterDialogClose;
